import React from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import {
  SelectElement,
  TextFieldElement,
  TextareaAutosizeElement,
  useForm,
  SwitchElement,
} from "react-hook-form-mui";
import { z } from "zod";

import { zodResolver } from "@hookform/resolvers/zod";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { getLLMProject } from "../graphql/queries";
import { GetLLMProjectQuery } from "../API";
import { GraphQLResult } from "@aws-amplify/api";
import { updateLLMProjectAfterEdit } from "../dataaccess/updateProjectAfterEdit";
import { useUser } from "../contexts/useUser";

const initialValues = {
  name: "",
  status: "active",
  description: "",
  newCustomerTokenCount: 1000,
  cacheResponsesForSeconds: 3600,
  promptBlacklist: "",
  loadSheddingTier: 0,
  newCustomerLoadSheddingTier: 0,
  maintenanceMode: false,
  maintenanceModeMessage:
    "We are currently performing maintenance on this service. Please try again later.",
  loadBalancingMode: "failover",
  systemInstructions:
    "Please follow these guidelines when generating responses:\n1. **Respect and Inclusivity**: Be respectful and avoid hate speech, racism, sexism, or discriminatory language. Use gender-neutral terms and avoid assumptions about personal characteristics.\n2. **Safety and Appropriateness**: Ensure responses are safe and suitable for all audiences; avoid harmful or dangerous advice.\n3. **Accuracy**: Provide accurate information. If unsure, indicate the need for more information or state the uncertainty.\n4. **Confidentiality**: Do not share personal or sensitive information; respect privacy and confidentiality.",
};

const schema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "Name is required"),
  status: z.string().min(1, "Status is required"),
  description: z.string().optional().nullable(),
  newCustomerTokenCount: z.coerce
    .number()
    .int()
    .nonnegative()
    .nullable()
    .optional(),
  cacheResponsesForSeconds: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .optional(),

  promptBlacklist: z.string().optional().nullable(),

  loadSheddingTier: z.coerce.number().int().nonnegative(),
  newCustomerLoadSheddingTier: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .nullable(),

  maintenanceMode: z.boolean(),
  maintenanceModeMessage: z.string().optional().nullable(),

  loadBalancingMode: z.string(),

  systemInstructions: z.string().optional().nullable(),
});

type ProjectFormType = z.infer<typeof schema>;

const ProjectForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logError } = useErrorLogging();
  const { trialDaysRemaining, subscriptionActive } = useUser();

  const queryClient = useQueryClient();
  const mutationUpdateProject = useMutation({
    mutationFn: updateLLMProjectAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });

      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error updating project", error as any);
    },
  });

  const {
    data: projectData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["projects", { id: id }],
    queryFn: () =>
      API.graphql(graphqlOperation(getLLMProject, { id: id })) as Promise<
        GraphQLResult<GetLLMProjectQuery>
      >,
    enabled: !!id,
  });

  const { handleSubmit, control, reset } = useForm<ProjectFormType>({
    values: projectData?.data?.getLLMProject || initialValues,
    resolver: zodResolver(schema),
  });

  if (id && isLoading) return <div>Loading...</div>;
  if (id && isError) return <div>Error loading project</div>;

  const handleUpdate = (values: ProjectFormType) => {
    if (!values) return;

    try {
      mutationUpdateProject.mutate({ id: id, ...values } as any);
    } catch (error) {
      logError("Error updating project", error as any);
    }
  };

  const handleClose = () => {
    window.history && window.history.length > 1 ? navigate(-1) : navigate("/");
  };

  const onSubmit = (data: ProjectFormType) => {
    if (id) handleUpdate(data);
  };

  return (
    <Paper sx={{ margin: 2, padding: 2, borderRadius: 3, border: "1px solid #444" }}>
      <Typography padding={2} variant="h6">
        Project Settings
      </Typography>
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="name"
            label="Name"
            size="small"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="status"
            label="Project status"
            size="small"
            required
            fullWidth
            options={[
              { id: "active", label: "Active" },
              { id: "disabled", label: "Disabled" },
              {
                id: "errors",
                label: "Disabled (due to errors)",
              },
              { id: "deleted", label: "Deleted" },
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <TextFieldElement
            control={control}
            name="description"
            label="Description"
            size="small"
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <TextareaAutosizeElement
            control={control}
            name="systemInstructions"
            label="System Instructions (use to always include brand and policy information to prompts sent to LLMs)"
            size="small"
            fullWidth
            rows={3}
          />
        </Grid>

        <Grid item xs={12}>
          <hr />
          <Typography variant="caption">operational settings</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="cacheResponsesForSeconds"
            label="Response Caching"
            size="small"
            required
            fullWidth
            options={[
              { id: 0, label: "Off" },
              { id: 60, label: "1 minute" },
              {
                id: 900,
                label: "15 minutes",
              },
              { id: 1800, label: "30 minutes" },
              { id: 3600, label: "1 hour" },
              { id: 86400, label: "1 day" },
              { id: 604800, label: "1 week" },
            ]}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="loadBalancingMode"
            label="Load Balancing / Failover"
            size="small"
            required
            fullWidth
            options={[
              {
                id: "off",
                label: "No failover",
              },
              {
                id: "failover",
                label: "Try other active services if first attempt fails.",
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="loadSheddingTier"
            label="Load Shedding Tier "
            size="small"
            required
            fullWidth
            options={[
              { id: "0", label: "All customer tiers are processed" },
              { id: "1", label: "Shed Tier 1 customers" },
              {
                id: "2",
                label: "Shed Tier 1 and Tier 2 customers",
              },
              { id: "3", label: "Shed Tier 1, 2 and 3 customers" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}></Grid>
        <Grid item xs={3}>
          <SwitchElement
            control={control}
            name="maintenanceMode"
            label={(!subscriptionActive && trialDaysRemaining <= 0) ? "Maintenance Mode (trial expired)": "Maintenance Mode"}
            disabled={!subscriptionActive && trialDaysRemaining <= 0}
          />
        </Grid>

        <Grid item xs={9}>
          <TextareaAutosizeElement
            control={control}
            name="maintenanceModeMessage"
            label="Maintenance Mode Message"
            size="small"
            fullWidth
            rows={3}
          />
        </Grid>

        <Grid item xs={12}>
          <hr />
          <Typography variant="caption">new customer settings</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="newCustomerLoadSheddingTier"
            label="New Customer Default Load Shedding Tier "
            size="small"
            required
            fullWidth
            options={[
              { id: 1, label: "Tier 1 (first to shed)" },
              {
                id: 2,
                label: "Tier 2 (customers shed after Tier 1)",
              },
              { id: 3, label: "Tier 3 customers (shed as last resort)" },
              { id: 4, label: "Never shed" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="newCustomerTokenCount"
            label="New Customer Initial Token Count Grant (blank for none)"
            size="small"
            type="number"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <hr />
        </Grid>
      </Grid>
      <Box sx={{ padding: 2 }}>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant={"contained"}
          sx={{ marginRight: 2 }}
        >
          Save and Close
        </Button>
        <Button onClick={() => reset()} variant={"outlined"}>
          Reset
        </Button>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};

export default ProjectForm;
